<script>
import { counterMixin } from '@mixins/counter-mixin';
import { workoutComputed } from '@/src/state/helpers';

export default {
  mixins: [counterMixin],
  data() {
    return {};
  },
  computed: {
    ...workoutComputed,
  },
  methods: {
    totalCircuitTime(nextCircuit) {
      const circuitTime = nextCircuit.exercises
        .map((exercise) => {
          return exercise.setDirections.map((set) => set.time);
        })
        .flat()
        .reduce((a, b) => a + b, 0);
      return this.$_getFormattedSeconds(circuitTime);
    },
    nextSetTime(nextCircuit) {
      const sets = nextCircuit.exercises[0].setDirections;
      return nextCircuit && sets.length ? `${this.$_getFormattedSeconds(sets[0].time)} ・ ` : 0;
    },
    nextSetReps(nextCircuit) {
      const sets = nextCircuit.exercises[0].setDirections;
      return nextCircuit && sets.length ? `${sets[0].reps} Reps ・ ` : 0;
    },
    nextSetWeight(nextCircuit) {
      const sets = nextCircuit.exercises[0].setDirections;
      return nextCircuit && sets.length ? `${sets[0].weight} Weight ・ ` : 0;
    },
  },
};
</script>

<template>
  <div class="details-section">
    <div class="content">
      <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-check'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
      <p class="finished-exercise" v-if="currentCircuit"> Circuit Complete </p>
      <p class="sub-title">
        {{ currentCircuit.description }}
      </p>
      <div class="radio-options">
        <BaseInputRadioButton
          v-for="(exercise, i) in currentCircuit.exercises"
          classStyle="container-radio"
          :key="i"
          :id="i"
          :message="exercise.label"
          :checked="true"
        />
      </div>
      <div class="trainer-feedback" v-if="currentCircuit.trainerFeedback.outro.text">
        <p>{{ currentCircuit.trainerFeedback.outro.text }}</p>
      </div>
    </div>
    <div class="up-next-container">
      <div class="col-9 up-next">
        <p>Up Next:</p>
        <template v-if="nextCircuit">
          <p>{{ nextCircuit.label }}</p>
          <p>
            <span>
              <span v-if="nextSetTime(nextCircuit)">{{ nextSetTime(nextCircuit) }}</span>
              <span v-if="nextSetReps(nextCircuit)">{{ nextSetReps(nextCircuit) }}</span>
              <span v-if="nextSetWeight(nextCircuit)">{{ nextSetWeight(nextCircuit) }}</span>
            </span>
            <template v-if="nextCircuit.exercises[0].requiredEquipment.unweighted.length">
              <span v-for="(equipment, i) in nextCircuit.exercises[0].requiredEquipment.unweighted" :key="i">
                {{ equipment.label }}<span v-if="i + 1 < nextCircuit.exercises[0].requiredEquipment.unweighted.length">,</span>
              </span>
            </template>
            <span v-if="nextCircuit.exercises[0].requiredEquipment.weighted">
              {{ nextCircuit.exercises[0].requiredEquipment.weighted.label }}</span
            >
          </p>
        </template>
        <p v-else>End Workout</p>
      </div>
      <BaseButton @click="$emit('continue')" class="button btn-primary large">Continue</BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.details-section {
  align-items: center;
  color: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding-bottom: 20px;
  padding-top: 20px;
  width: 100%;
  .content {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 75%;

    @include media('xl', null) {
      width: 65%;
    }

    .icon-check {
      path {
        &:first-child {
          fill: linear-gradient(180deg, rgba(85, 188, 245, 0.6) 0%, rgba(62, 79, 226, 0.6) 100%);
        }
      }
    }

    .finished-exercise {
      color: white;
      font-family: $base-font-family;
      font-size: 24px;
      font-style: normal;
      font-weight: bold;
      line-height: 33px;
      margin-top: 3rem;

      @media screen and (min-width: 1100px) {
        @include calc-font-size(2.7);
      }
    }

    .sub-title {
      color: white;
      font-family: $proxima-nova;
      font-size: 18px;
      font-style: normal;
      font-weight: 300;
      line-height: 21px;
      margin-bottom: 2rem;
      mix-blend-mode: normal;
      opacity: 0.7;
      text-align: center;
    }

    .radio-options {
      margin-bottom: 1.6rem;
      width: 100%;

      .container-radio-check {
        align-items: center;
        background: rgba(196, 196, 196, 0.2);
        border: 1px solid transparent;
        border-radius: 8px;
        display: flex;
        height: 50px;
        margin: 0 auto;
        margin-bottom: 12px;
        padding: 16px 14px 16px 16.21px;
        width: auto;

        ::v-deep .checkbox-label {
          font-family: $secondary-font-family;
          margin: 0;

          @include media('xxl', null) {
            margin-left: 14.15px;
          }

          label {
            color: white;
            font-family: $proxima-nova;
            font-size: 16px;
            font-style: normal;
            font-weight: normal;
            line-height: 19px;
            margin-bottom: unset;
            text-transform: capitalize;

            @include media('xxl', null) {
              margin-left: 1rem;
            }
          }
        }

        ::v-deep .round {
          display: none;
          position: relative;

          @include media('xxl', null) {
            display: block;
          }
        }

        ::v-deep .round label {
          background-color: white;
          border: 1px solid #ccc;
          border-radius: 50%;
          cursor: unset;
          height: 28px;
          left: 0;
          margin-bottom: unset;
          position: absolute;
          top: -0.7rem;
          width: 28px;
        }

        ::v-deep .round label::after {
          border: 3px solid #fff;
          border-right: none;
          border-top: none;
          content: '';
          height: 8px;
          left: 5px;
          opacity: 0;
          position: absolute;
          top: 8px;
          transform: rotate(-45deg);
          width: 15px;
        }

        ::v-deep .round input[type='checkbox'] {
          visibility: hidden;
        }

        ::v-deep .round input[type='checkbox']:checked + label {
          background-color: #4059e1;
          border-color: #4059e1;
        }

        ::v-deep .round input[type='checkbox']:checked + label::after {
          opacity: 1;
        }
      }
    }

    .trainer-feedback {
      margin: 3rem auto 3rem;

      p {
        color: white;
        font-size: 17px;
        font-style: normal;
        font-weight: 300;
        line-height: 114.3%;
        mix-blend-mode: normal;
        text-align: center;
      }
    }
  }

  .up-next-container {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    .up-next {
      margin-bottom: 3rem;
      width: 50%;

      p {
        margin-bottom: 0.5rem;
        margin-left: auto;
        margin-right: auto;

        &:first-child {
          color: white;
          font-family: $proxima-nova;
          font-size: 12px;
          font-style: normal;
          font-weight: 600;
          line-height: 15px;

          @media screen and (min-width: 1100px) {
            @include calc-font-size(0.9);
          }
        }

        &:nth-child(2) {
          color: white;
          font-family: $roboto;
          font-size: 15px;
          font-style: normal;
          font-weight: bold;
          letter-spacing: 0.5px;
          line-height: 18px;
          overflow: hidden;
          position: relative;
          text-overflow: ellipsis;
          white-space: nowrap;

          @media screen and (min-width: 1100px) {
            @include calc-font-size(1.3);
          }
        }

        &:nth-child(3) {
          color: #f2f2f2;
          font-family: $proxima-nova;
          font-size: 14px;
          font-style: normal;
          font-weight: 300;
          line-height: 17px;
          mix-blend-mode: normal;
          opacity: 0.7;
          text-transform: capitalize;

          @media screen and (min-width: 1100px) {
            @include calc-font-size(1);
          }
        }
      }
    }
  }

  .btn-primary {
    @include calc-font-size(1.4);

    background-color: transparent;
    border: 1px white solid;
    line-height: unset;
    transition-duration: 0.3s;
    width: 50%;

    &:hover {
      background-color: rgba(#1c1c1c, 0.3);
      border: 1px rgba(#1c1c1c, 0.3) solid;
    }
  }
}
</style>
